import React from 'react';
import { Badge } from '@/components/ui/badge';

// Premium Color Palette from PREMIUM_METRIC_PATTERNS.md
const PREMIUM_COLORS = {
  primaryGolfGreen: '#1B4332',
  secondaryGolfGreen: '#2D5A3D',
  accentBrightGreen: '#52B788',
  successGreen: '#16A34A',
  errorRed: '#DC2626',
  neutralGray: '#9CA3AF',
  darkText: '#374151'
};

export interface LeaderboardScoreDisplayProps {
  score: number;
  scoreDifferential?: number;
  par?: number;
  showDifferential?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'prominent' | 'compact';
  className?: string;
}

/**
 * Unified Score Display Component
 * 
 * Consolidates score formatting and display logic used across all leaderboards:
 * - Consistent score formatting (total score + differential)
 * - Color-coded score differentials (under par = green, over par = red)
 * - Proper par differential calculations and display
 * - Multiple display variants for different contexts
 * - Responsive sizing options
 */
export function LeaderboardScoreDisplay({
  score,
  scoreDifferential,
  par,
  showDifferential = true,
  size = 'md',
  variant = 'default',
  className = ''
}: LeaderboardScoreDisplayProps) {

  // Calculate differential if not provided but par is available
  const differential = scoreDifferential ?? (par ? score - par : undefined);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': 
        return {
          score: 'text-sm font-medium',
          differential: 'text-xs'
        };
      case 'lg':
        return {
          score: 'text-xl font-bold',
          differential: 'text-sm'
        };
      default:
        return {
          score: 'text-lg font-semibold',
          differential: 'text-xs'
        };
    }
  };

  const getDifferentialColor = (diff: number) => {
    if (diff < 0) return PREMIUM_COLORS.successGreen; // Under par - green
    if (diff > 0) return PREMIUM_COLORS.errorRed;     // Over par - red
    return PREMIUM_COLORS.neutralGray;                // Even par - gray
  };

  const getDifferentialBadgeVariant = (diff: number): "default" | "secondary" | "destructive" | "outline" => {
    if (diff < 0) return "default"; // Green for under par
    if (diff > 0) return "destructive"; // Red for over par
    return "secondary"; // Gray for even par
  };

  const formatDifferential = (diff: number): string => {
    if (diff === 0) return 'E'; // Even par
    if (diff > 0) return `+${diff}`; // Over par
    return `${diff}`; // Under par (already has negative sign)
  };

  const sizeClasses = getSizeClasses();

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className={sizeClasses.score} style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
          {score}
        </span>
        {showDifferential && differential !== undefined ? <Badge 
          variant={getDifferentialBadgeVariant(differential)}
          className={`${sizeClasses.differential} px-1.5 py-0.5`}
                                                          >
          {formatDifferential(differential)}
                                                          </Badge> : null}
      </div>
    );
  }

  if (variant === 'prominent') {
    return (
      <div className={`text-center ${className}`}>
        <div className={sizeClasses.score} style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
          {score}
        </div>
        {showDifferential && differential !== undefined ? <div 
          className={`${sizeClasses.differential} font-medium mt-1`}
          style={{ color: getDifferentialColor(differential) }}
                                                          >
          {formatDifferential(differential)}
                                                          </div> : null}
      </div>
    );
  }

  // Default variant
  return (
    <div className={`flex flex-col items-end ${className}`}>
      <span className={sizeClasses.score} style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
        {score}
      </span>
      {showDifferential && differential !== undefined ? <span 
        className={`${sizeClasses.differential} font-medium`}
        style={{ color: getDifferentialColor(differential) }}
                                                        >
        {formatDifferential(differential)}
                                                        </span> : null}
    </div>
  );
}

/**
 * Utility function to get score differential color (for legacy compatibility)
 */
export function getScoreDifferentialColor(differential: number): string {
  if (differential < 0) return PREMIUM_COLORS.successGreen;
  if (differential > 0) return PREMIUM_COLORS.errorRed;
  return PREMIUM_COLORS.neutralGray;
}

/**
 * Utility function to format score differential (for legacy compatibility)
 */
export function formatScoreDifferential(differential: number): string {
  if (differential === 0) return 'E';
  if (differential > 0) return `+${differential}`;
  return `${differential}`;
}

/**
 * Utility function to determine if score is under par
 */
export function isUnderPar(score: number, par: number): boolean {
  return score < par;
}

/**
 * Utility function to determine if score is over par
 */
export function isOverPar(score: number, par: number): boolean {
  return score > par;
}